import { PICK_SHEET_MAX_ORDERS } from "@loot-os/fulfillment-core/order/packing-fold";
import {
  FULFILLMENT_METHODS,
  type FulfillmentMethod,
  ORDER_STATUSES,
  ORDER_TYPES,
  type OrderStatus,
  type OrderType,
} from "@loot-os/fulfillment-core/order/types";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import { Checkbox } from "@loot-os/ui/components/checkbox";
import { DataTable, selectionColumn } from "@loot-os/ui/components/data-table";
import { Input } from "@loot-os/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import { downloadCsv, toCsv } from "@loot-os/ui/lib/csv";
import { createDataTableColumns, type RowSelectionState } from "@loot-os/ui/lib/dataTable";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Binoculars, Check, SquareArrowOutUpRight } from "lucide-react";
import { useMemo, useState } from "react";
import { ErrorAlert } from "../../components/ErrorAlert";
import { FilterField } from "../../components/FilterField";
import { LoadMoreButton } from "../../components/LoadMoreButton";
import { OrderCardsDialog } from "../../components/OrderCardsDialog";
import { OrderTag } from "../../components/OrderTag";
import { centsToDollars, formatDate } from "../../lib/format";
import {
  type OrderListRow,
  orderStatusTagClass,
  packingSubstatusLabel,
  packingTagClass,
  statusTagText,
} from "../../lib/fulfillmentOrders";
import { FULFILLMENT_WIDE_PANEL_CLASS } from "../../lib/fulfillmentPanel";
import {
  isSelectionActionEnabled,
  ORDER_LIST_SELECTION_ACTIONS,
  selectionActionLabel,
} from "../../lib/orderActions";
import { OpenOrderCardsContext, useOpenOrderCards } from "../../lib/orderCards";
import { exportFileName, ordersExportRows, pullSheetRows } from "../../lib/orderExports";
import { openOrdersInTabs } from "../../lib/orderTabs";
import {
  exportPackingSlips,
  exportThermalLabels,
  type ThermalLabelOrder,
} from "../../lib/packingExports";
import { useTRPC } from "../../lib/trpc";
import { useDebounced } from "../../lib/useDebounced";

const PAGE_SIZE = 50;

/** Select has no unset value, so an explicit member stands for "no filter". */
const ANY = "__any";

const STATUS_LABELS: Record<string, string> = {
  [ANY]: "All statuses",
  ...Object.fromEntries(ORDER_STATUSES.map((status) => [status, statusTagText(status)])),
};

const TYPE_LABELS: Record<string, string> = {
  [ANY]: "All",
  ...Object.fromEntries(ORDER_TYPES.map((type) => [type, type])),
};

const METHOD_LABELS: Record<string, string> = {
  [ANY]: "All methods",
  ...Object.fromEntries(FULFILLMENT_METHODS.map((method) => [method, method])),
};

/** Retool's "Filter SubStatus": narrows the loaded rows, like Retool's did. */
const SUBSTATUS_LABELS: Record<string, string> = {
  [ANY]: "Any",
  READY_TO_SHIP: "READY TO SHIP",
  PARTIALLY_PACKED: "PARTIAL PACKED",
  NOT_PACKED: "NOT PACKED",
};

type Row = OrderListRow;

const columns = createDataTableColumns<Row>();
const PINNED = { start: ["select"], end: ["actions"] };
/**
 * A row's games. An API deployed before Categories sends none, and a page loaded from the new
 * build can read it during the rollout; an empty list keeps the table up.
 */
const rowCategories = (row: Row): string[] => row.categories ?? [];
const dollars = (value: unknown) => centsToDollars(Number(value));
/** Dates arrive as ISO strings, which sort and export as they are. */
const dateCell = ({ getValue }: { getValue: () => string | null }) => (
  <span className="whitespace-nowrap">{formatDate(getValue())}</span>
);

/**
 * The columns in TCG Order List's order, at Retool's widths. Retool's hidden-by-default columns
 * (shipped, processing and cancelled at, cancellation reason, shipping fee, tracking, cancelled
 * count, type) are `hidden`: in the CSV, not on screen. Tenant and Method are Loot OS's own, shown
 * last. Every cell is one line, so a row is as short as Retool's.
 */
const ORDER_COLUMNS = columns.columns([
  selectionColumn<Row>(),
  columns.accessor("createdAt", { header: "Created at", size: 170, cell: dateCell }),
  columns.accessor((row) => row.customerName ?? "", {
    id: "name",
    header: "Name",
    size: 140,
    cell: ({ getValue }) => getValue() || "—",
    meta: { filter: "text" },
  }),
  columns.accessor("status", {
    header: "Status",
    size: 110,
    cell: ({ getValue }) => (
      <OrderTag className={orderStatusTagClass(getValue())}>{statusTagText(getValue())}</OrderTag>
    ),
    meta: { filter: "text" },
  }),
  columns.accessor((row) => packingSubstatusLabel(row.packingSubstatus), {
    id: "subStatus",
    header: "SubStatus",
    size: 130,
    cell: ({ row, getValue }) =>
      row.original.packingSubstatus ? (
        <OrderTag className={packingTagClass(row.original.packingSubstatus)}>{getValue()}</OrderTag>
      ) : (
        "—"
      ),
    meta: { filter: "text" },
  }),
  columns.accessor("updatedAt", { header: "Updated at", size: 170, cell: dateCell }),
  columns.accessor((row) => rowCategories(row).join(", "), {
    id: "categories",
    header: "Categories",
    size: 150,
    cell: ({ row }) =>
      rowCategories(row.original).length ? (
        <div className="flex gap-1 overflow-hidden" title={rowCategories(row.original).join(", ")}>
          {rowCategories(row.original).map((category) => (
            <OrderTag key={category} className="bg-muted text-muted-foreground">
              {category}
            </OrderTag>
          ))}
        </div>
      ) : (
        "—"
      ),
    meta: { filter: "text" },
  }),
  columns.accessor((row) => row.location ?? "", {
    id: "location",
    header: "Location",
    size: 206,
    cell: ({ getValue }) => getValue() || "—",
    meta: { filter: "text" },
  }),
  columns.accessor("itemCount", {
    header: "Item Count",
    size: 100,
    meta: { align: "end", filter: "number" },
  }),
  columns.accessor("expectedCostCents", {
    header: "Expected order cost",
    size: 150,
    cell: ({ getValue }) => centsToDollars(getValue()),
    meta: { align: "end", csv: dollars, filter: "number", filterCents: true },
  }),
  columns.accessor("id", {
    header: "ID",
    size: 280,
    cell: ({ getValue }) => (
      <Link
        className="font-mono text-xs underline underline-offset-4"
        to="/fulfillment/orders/$orderId"
        params={{ orderId: getValue() }}
      >
        {getValue()}
      </Link>
    ),
  }),
  columns.accessor((row) => row.endUserExternalId ?? row.endUserId, {
    id: "accountId",
    header: "Account ID",
    size: 220,
    cell: ({ row, getValue }) => (
      <Link
        className="font-mono text-xs underline underline-offset-4"
        to="/tenants/$tenantId/users/$userId"
        params={{ tenantId: row.original.tenantId, userId: row.original.endUserId }}
        title={row.original.endUserId}
      >
        {getValue()}
      </Link>
    ),
    meta: { filter: "text" },
  }),
  columns.accessor((row) => (row.accountLocked ? "true" : "false"), {
    id: "locked",
    header: "Is account locked",
    size: 130,
    cell: ({ row }) =>
      row.original.accountLocked ? <Check className="size-4 text-destructive" /> : null,
    meta: { filter: "text" },
  }),
  columns.accessor((row) => row.tenantName ?? row.tenantId, {
    id: "tenant",
    header: "Tenant",
    cell: ({ row, getValue }) => <span title={row.original.tenantId}>{getValue()}</span>,
    meta: { filter: "text" },
  }),
  columns.accessor((row) => row.method ?? "", {
    id: "method",
    header: "Method",
    cell: ({ getValue }) => getValue() || "—",
    meta: { filter: "text" },
  }),
  columns.accessor("shippedAt", { header: "Shipped at", meta: { hidden: true } }),
  columns.accessor("processingAt", { header: "Processing at", meta: { hidden: true } }),
  columns.accessor("cancelledAt", { header: "Cancelled at", meta: { hidden: true } }),
  columns.accessor((row) => row.cancellationReason ?? "", {
    id: "cancellationReason",
    header: "Cancellation reason",
    meta: { hidden: true },
  }),
  columns.accessor("shippingFee", {
    header: "Shipping fee",
    meta: { hidden: true, csv: dollars },
  }),
  columns.accessor((row) => row.trackingNumber ?? "", {
    id: "tracking",
    header: "Tracking number",
    meta: { hidden: true },
  }),
  columns.accessor("cancelledItemCount", {
    header: "Cancelled Item Count",
    meta: { hidden: true },
  }),
  columns.accessor("type", { header: "Type", meta: { hidden: true } }),
  // Retool's row actions: View Cards and Go To Order, which opens a new tab. Its third, Claim
  // Fulfillment, is hidden there and left out here.
  columns.display({
    id: "actions",
    header: "",
    size: 68,
    cell: ({ row }) => (
      <div className="flex items-center justify-end gap-1">
        <ViewCardsButton orderId={row.original.id} />
        <Link
          to="/fulfillment/orders/$orderId"
          params={{ orderId: row.original.id }}
          target="_blank"
          rel="noreferrer"
          aria-label="Go to order"
          title="Go To Order"
          className={buttonVariants({ variant: "ghost", size: "icon-xs" })}
        >
          <SquareArrowOutUpRight />
        </Link>
      </div>
    ),
  }),
]);

/** A row's View Cards: the card view over that one order. */
function ViewCardsButton({ orderId }: { orderId: string }) {
  const openCards = useOpenOrderCards();
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      aria-label="View cards"
      title="View Cards"
      onClick={() => openCards([orderId])}
    >
      <Binoculars />
    </Button>
  );
}

/** A button placed with Retool's enable rules whose action is not built yet. */
function notBuilt(action: string) {
  toast(`${action} is not built yet.`);
}

/**
 * Prints a label for each order that still has a card to ship. A label over an empty Items table
 * (every line cancelled) can end up on a parcel, so those orders are left out and the toast says
 * how many.
 */
function exportThermalLabelsWithCards(orders: ThermalLabelOrder[]) {
  const labelled = orders.filter((order) => order.cards.length > 0);
  const skipped = orders.length - labelled.length;
  if (labelled.length === 0) {
    toast.error("None of the selected orders has a card left to ship. Nothing was downloaded.");
    return;
  }
  exportThermalLabels(labelled);
  if (skipped > 0) {
    toast(`Left out ${skipped} order${skipped === 1 ? "" : "s"} with no cards left to ship.`);
  }
}

/** The selection buttons that download a file. */
type SelectionExport = Exclude<
  (typeof ORDER_LIST_SELECTION_ACTIONS)[number],
  "Open Combined Card View" | "Open in Tabs"
>;

/** A local calendar day as the instant it starts; `nextDay` for an inclusive "to". */
function dayStart(day: string, nextDay = false): string | undefined {
  if (!day) return undefined;
  const date = new Date(`${day}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  if (nextDay) date.setDate(date.getDate() + 1);
  return date.toISOString();
}

/**
 * The table's height: Retool's fixed table filled the window under its one row of filters, and
 * scrolls its own rows, so the searches and the selection buttons sit a scroll below it.
 */
const TABLE_MAX_HEIGHT = "max(24rem, calc(100dvh - 11rem))";

/**
 * Fulfillment orders across every tenant: the queue a fulfiller works from, laid out like the
 * Retool TCG Order List it replaces (one row of queue filters, the table at the window's height,
 * then the searches, View Missing Cards and the selection buttons below), so the two read the
 * same side by side. Every filter but SubStatus runs on the server, over every order; the table's
 * own search and filter builder narrow the loaded rows.
 */
export function OrdersPage() {
  const trpc = useTRPC();
  // Retool's defaults: processing TCG orders, newest first, locked accounts hidden.
  const [status, setStatus] = useState<OrderStatus | typeof ANY>("PROCESSING");
  const [type, setType] = useState<OrderType | typeof ANY>("TCG");
  // MANUAL_BUY is the fulfiller's own queue of marketplace buys; BUY_BOT is a bot's.
  const [method, setMethod] = useState<FulfillmentMethod | typeof ANY>(ANY);
  // Every tenant's orders by default; the tool spans tenants. An account lookup keeps this filter.
  const [tenantId, setTenantId] = useState<string>(ANY);
  const tenants = useQuery({ ...trpc.platform.tenants.list.queryOptions(), staleTime: 60_000 });
  const tenantLabels = useMemo(
    () => ({
      [ANY]: "All tenants",
      ...Object.fromEntries(
        [...(tenants.data ?? [])]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((tenant) => [tenant.id, tenant.name]),
      ),
    }),
    [tenants.data],
  );
  const [customerName, setCustomerName] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [itemName, setItemName] = useState("");
  const [accountId, setAccountId] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [subStatus, setSubStatus] = useState<string>(ANY);
  const [oldestFirst, setOldestFirst] = useState(false);
  const [showLocked, setShowLocked] = useState(false);
  const [selection, setSelection] = useState<RowSelectionState>({});
  // The orders the Cards View is open over; null while it is closed.
  const [cardsFor, setCardsFor] = useState<string[] | null>(null);

  const text = {
    customerName: useDebounced(customerName.trim(), 300),
    trackingNumber: useDebounced(trackingNumber.trim(), 300),
    itemName: useDebounced(itemName.trim(), 300),
    accountId: useDebounced(accountId.trim(), 300),
  };

  const orders = useInfiniteQuery(
    trpc.platform.fulfillment.orders.search.infiniteQueryOptions(
      {
        limit: PAGE_SIZE,
        statuses: status === ANY ? undefined : [status],
        types: type === ANY ? undefined : [type],
        methods: method === ANY ? undefined : [method],
        tenantId: tenantId === ANY ? undefined : tenantId,
        customerName: text.customerName || undefined,
        trackingNumber: text.trackingNumber || undefined,
        itemName: text.itemName || undefined,
        accountId: text.accountId || undefined,
        createdFrom: dayStart(createdFrom),
        createdTo: dayStart(createdTo, true),
        hideLockedAccounts: !showLocked,
        oldestFirst,
      },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const loaded: Row[] = useMemo(
    () => orders.data?.pages.flatMap((page) => page.orders) ?? [],
    [orders.data],
  );
  const rows = useMemo(
    () => (subStatus === ANY ? loaded : loaded.filter((row) => row.packingSubstatus === subStatus)),
    [loaded, subStatus],
  );

  // Naming an end user sets aside the queue filters on the server (dropQueueFiltersForAccount), so
  // their controls go inert, and the totals leave out cancelled orders, as Retool's did.
  // The debounced value, the one the query uses, so the inert controls and the totals rule always
  // describe the rows on screen, not the ones the next query will bring.
  const lookingUpAccount = text.accountId !== "";
  const selected = rows.filter((row) => selection[row.id]);
  const totalled = (selected.length ? selected : rows).filter(
    (row) => !lookingUpAccount || row.status !== "CANCELLED",
  );
  const itemsTotal = totalled.reduce((sum, row) => sum + row.expectedCostCents, 0);
  const shippingTotal = totalled.reduce((sum, row) => sum + row.shippingFee, 0);

  /** Retool's Open Combined Card View, capped where the cards read is. */
  const openCombinedCards = () => {
    if (selected.length > PICK_SHEET_MAX_ORDERS) {
      toast.error(`Select at most ${PICK_SHEET_MAX_ORDERS} orders for the card view.`);
      return;
    }
    setCardsFor(selected.map((row) => row.id));
  };

  const queryClient = useQueryClient();
  /**
   * The exports, each read from the server when pressed, not from the page: the CSVs, the printed
   * slips Packing prints too, and the thermal labels.
   */
  const exportSelection = async (label: SelectionExport) => {
    if (selected.length > PICK_SHEET_MAX_ORDERS) {
      toast.error(`Select at most ${PICK_SHEET_MAX_ORDERS} orders to export.`);
      return;
    }
    const orderIds = selected.map((row) => row.id);
    switch (label) {
      case "Export Orders": {
        const orders = await queryClient.fetchQuery(
          trpc.platform.fulfillment.orders.ordersExport.queryOptions({ orderIds }),
        );
        downloadCsv(exportFileName("export-orders"), toCsv(ordersExportRows(orders)));
        return;
      }
      case "Export Thermal Labels": {
        const orders = await queryClient.fetchQuery(
          trpc.platform.fulfillment.orders.ordersExport.queryOptions({ orderIds }),
        );
        exportThermalLabelsWithCards(orders);
        return;
      }
      case "Export Packing Slips": {
        const slips = await queryClient.fetchQuery(
          trpc.platform.fulfillment.orders.packingSlips.queryOptions({ orderIds }),
        );
        exportPackingSlips(slips);
        return;
      }
      case "Export Pull Sheet": {
        const pullSheet = await queryClient.fetchQuery(
          trpc.platform.fulfillment.orders.pullSheet.queryOptions({ orderIds }),
        );
        downloadCsv(exportFileName("export-pullSheet"), toCsv(pullSheetRows(pullSheet)));
        return;
      }
      default: {
        // A new action in the list fails to compile here rather than downloading another's file.
        const unhandled: never = label;
        notBuilt(unhandled);
      }
    }
  };

  const runSelectionAction = (label: (typeof ORDER_LIST_SELECTION_ACTIONS)[number]) => {
    if (label === "Open Combined Card View") return openCombinedCards();
    if (label === "Open in Tabs") return openOrdersInTabs(selected.map((row) => row.id));
    exportSelection(label).catch((error: unknown) =>
      toast.error(error instanceof Error ? error.message : `${label} failed.`),
    );
  };

  return (
    <OpenOrderCardsContext.Provider value={setCardsFor}>
      <div className={`${FULFILLMENT_WIDE_PANEL_CLASS} flex flex-col gap-3`}>
        <h1 className="sr-only">Orders</h1>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          <FilterField id="order-status" label="Order Status" inline="compact" className="w-72">
            <LabelledSelect
              id="order-status"
              labels={STATUS_LABELS}
              value={status}
              disabled={lookingUpAccount}
              onChange={(value) => setStatus(value as OrderStatus)}
            />
          </FilterField>
          <FilterField id="order-end-user" label="Account ID" inline="compact" className="w-64">
            <Input
              id="order-end-user"
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
              placeholder="User Account ID"
              title="Shows every order of the account, whatever the other filters say"
            />
          </FilterField>
          <FilterField id="order-type" label="Type" inline="compact" className="w-44">
            <LabelledSelect
              id="order-type"
              labels={TYPE_LABELS}
              value={type}
              disabled={lookingUpAccount}
              onChange={(value) => setType(value as OrderType)}
            />
          </FilterField>
          <label
            htmlFor="order-oldest-first"
            className="flex items-center gap-2 text-sm font-semibold whitespace-nowrap"
          >
            <Checkbox
              id="order-oldest-first"
              checked={oldestFirst}
              onCheckedChange={(checked) => setOldestFirst(checked === true)}
            />
            Oldest First
          </label>
          <FilterField id="order-tenant" label="Tenant" inline="compact" className="w-64">
            <LabelledSelect
              id="order-tenant"
              labels={tenantLabels}
              value={tenantId}
              onChange={setTenantId}
            />
          </FilterField>
          <p
            className="ml-auto text-sm whitespace-nowrap tabular-nums"
            title={
              selected.length ? "Totals of the selected orders" : "Totals of the loaded orders"
            }
          >
            Items: {centsToDollars(itemsTotal)}, Shipping: {centsToDollars(shippingTotal)}
            {selected.length ? " (selected)" : ""}
          </p>
        </div>

        {orders.error ? (
          <ErrorAlert message={orders.error.message} />
        ) : orders.isLoading ? (
          <Skeleton className="h-96 w-full" />
        ) : (
          <div className="flex flex-col gap-2">
            <DataTable
              columns={ORDER_COLUMNS}
              data={rows}
              maxHeight={TABLE_MAX_HEIGHT}
              getRowId={(row) => row.id}
              pinned={PINNED}
              rowSelection={selection}
              onRowSelectionChange={setSelection}
              search="Search loaded orders"
              csvName="orders"
              emptyMessage="No orders match these filters."
            />
            <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
              <span>
                Showing {formatNumber(rows.length)}
                {orders.hasNextPage ? "+" : ""} results
                {selected.length ? ` · ${formatNumber(selected.length)} selected` : ""}
              </span>
              <LoadMoreButton
                hasNextPage={orders.hasNextPage}
                isFetchingNextPage={orders.isFetchingNextPage}
                onClick={() => void orders.fetchNextPage()}
              />
            </div>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-12">
          <div className="flex flex-col gap-2 lg:col-span-5">
            <p className="text-sm">Note: these search every order, not just the loaded page</p>
            <FilterField id="order-item" label="Search Cards" inline="aligned">
              <Input
                id="order-item"
                value={itemName}
                onChange={(event) => setItemName(event.target.value)}
              />
            </FilterField>
            <FilterField id="order-customer" label="Search Customer" inline="aligned">
              <Input
                id="order-customer"
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
              />
            </FilterField>
            <FilterField id="order-tracking" label="Search Tracking" inline="aligned">
              <Input
                id="order-tracking"
                value={trackingNumber}
                onChange={(event) => setTrackingNumber(event.target.value)}
              />
            </FilterField>
            <FilterField id="order-created-from" label="Date Range" inline="aligned">
              <div className="flex items-center gap-2">
                <Input
                  id="order-created-from"
                  className="min-w-0"
                  type="date"
                  aria-label="Created from"
                  value={createdFrom}
                  onChange={(event) => setCreatedFrom(event.target.value)}
                />
                <span className="text-muted-foreground">–</span>
                <Input
                  className="min-w-0"
                  type="date"
                  aria-label="Created to"
                  value={createdTo}
                  onChange={(event) => setCreatedTo(event.target.value)}
                />
              </div>
            </FilterField>
            <FilterField id="order-substatus" label="Filter SubStatus" inline="aligned">
              <LabelledSelect
                id="order-substatus"
                labels={SUBSTATUS_LABELS}
                value={subStatus}
                onChange={setSubStatus}
              />
            </FilterField>
            <FilterField id="order-method" label="Method" inline="aligned">
              <LabelledSelect
                id="order-method"
                labels={METHOD_LABELS}
                value={method}
                disabled={lookingUpAccount}
                onChange={(value) => setMethod(value as FulfillmentMethod)}
              />
            </FilterField>
            <label
              htmlFor="order-show-locked"
              className="flex items-center gap-2 text-sm font-semibold"
            >
              <Checkbox
                id="order-show-locked"
                checked={showLocked || lookingUpAccount}
                disabled={lookingUpAccount}
                onCheckedChange={(checked) => setShowLocked(checked === true)}
              />
              Show Locked Accounts
            </label>
          </div>

          <div className="flex items-start lg:col-span-2 lg:col-start-7">
            <Button type="button" className="w-full" onClick={() => notBuilt("View Missing Cards")}>
              View Missing Cards
            </Button>
          </div>

          <div className="flex flex-col gap-2 lg:col-span-3 lg:col-start-10">
            <p className="text-sm">Note: these only apply to current selection</p>
            {ORDER_LIST_SELECTION_ACTIONS.map((label) => (
              <Button
                key={label}
                type="button"
                disabled={!isSelectionActionEnabled(selected.length)}
                onClick={() => runSelectionAction(label)}
              >
                {selectionActionLabel(label)}
              </Button>
            ))}
          </div>
        </div>
        <OrderCardsDialog orderIds={cardsFor} onClose={() => setCardsFor(null)} />
      </div>
    </OpenOrderCardsContext.Provider>
  );
}

function LabelledSelect({
  id,
  labels,
  value,
  disabled,
  onChange,
}: {
  id: string;
  labels: Record<string, string>;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Select
      items={labels}
      value={value}
      disabled={disabled}
      onValueChange={(next) => onChange(String(next))}
    >
      <SelectTrigger id={id} className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(labels).map(([key, label]) => (
          <SelectItem key={key} value={key}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
